import React from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import { Button, Container, Nav, Navbar } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

import HomePage from './pages/HomePage';
import JiraFreeAgentSyncPage from './pages/JiraFreeAgentSyncPage';
import LandingPageGenerator from './pages/LandingPageGenerator';
import ContractManager from './pages/ContractManager';
import WebsiteManager from './pages/WebsiteManager';
import JiraFreeAgentSetupPage from './pages/JiraFreeAgentSetupPage';
import FreeAgentProjectsEstimatesPage from './pages/FreeAgentProjectsEstimatesPage';
import LabourCostsPage from './pages/LabourCostsPage';
import ProjectsPage from './pages/ProjectsPage';
import CategoriesPage from './pages/CategoriesPage';
import LoginPage from './pages/LoginPage';
import OperationalAutomationsPage from './pages/OperationalAutomationsPage';
import IntegratedManagementSystemPage from './pages/IntegratedManagementSystemPage';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';

import './App.css';

interface NavigationItem {
  label: string;
  permission: string | string[];
  path: string;
}

const navigationItems: NavigationItem[] = [
  { label: 'Home', path: '/', permission: 'home.view' },
  { label: 'Jira FreeAgent Sync', path: '/jira-freeagent-sync', permission: 'jira-freeagent.manage' },
  { label: 'Landing Page Generator', path: '/landing-page-generator', permission: 'content.manage' },
  { label: 'Contract Manager', path: '/contract-manager', permission: 'contracts.manage' },
  { label: 'Website Manager', path: '/website-manager', permission: 'websites.manage' },
  { label: 'Jira/FreeAgent Setup', path: '/jira-freeagent-setup', permission: 'jira-freeagent.manage' },
  { label: 'Projects', path: '/projects', permission: 'projects.manage' },
  { label: 'Categories', path: '/categories', permission: 'categories.manage' },
  { label: 'Projects & Invoices', path: '/freeagent-projects-estimates', permission: 'finance.manage' },
  { label: 'Labour Costs', path: '/labour-costs', permission: 'labour-costs.manage' },
  { label: 'Operational Automations', path: '/operational-automations', permission: ['finance.manage', 'jira-freeagent.manage', 'projects.manage'] },
  { label: 'Integrated Management System', path: '/integrated-management-system', permission: ['integrated-management-system.manage', 'integrated-management-system.audit'] },
];

const ProtectedRoute: React.FC<{ children: React.ReactElement; permission: string | string[] }> = ({ children, permission }) => (
  <RequireAuth
    permission={typeof permission === 'string' ? permission : undefined}
    anyPermissions={Array.isArray(permission) ? permission : undefined}
  >
    {children}
  </RequireAuth>
);

const AppShell: React.FC = () => {
  const { user, logout, hasPermission } = useAuth();
  const visibleNavigationItems = navigationItems.filter((item) => (
    typeof item.permission === 'string'
      ? hasPermission(item.permission)
      : item.permission.some(hasPermission)
  ));

  return (
    <Router>
      <Navbar bg="light" expand={false}>
        <Container>
          <Navbar.Brand as={Link} to="/">
            <h1 style={{ fontFamily: 'Raleway, sans-serif' }}>Internal Gemstone Services</h1>
          </Navbar.Brand>
          {user && (
            <div className="gcm-user-summary">
              <span>{user.name}</span>
              <small>{user.roleLabel}</small>
            </div>
          )}
          {user && <Navbar.Toggle aria-controls="basic-navbar-nav" />}
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="mr-auto">
              {visibleNavigationItems.map((item) => (
                <Nav.Link as={Link} key={item.path} to={item.path}>
                  {item.label}
                </Nav.Link>
              ))}
              {user && (
                <Button className="gcm-logout-button" size="sm" variant="outline-secondary" onClick={logout}>
                  Sign out
                </Button>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<ProtectedRoute permission="home.view"><HomePage /></ProtectedRoute>} />
        <Route path="/jira-freeagent-sync" element={<ProtectedRoute permission="jira-freeagent.manage"><JiraFreeAgentSyncPage /></ProtectedRoute>} />
        <Route path="/landing-page-generator" element={<ProtectedRoute permission="content.manage"><LandingPageGenerator /></ProtectedRoute>} />
        <Route path="/contract-manager" element={<ProtectedRoute permission="contracts.manage"><ContractManager /></ProtectedRoute>} />
        <Route path="/website-manager" element={<ProtectedRoute permission="websites.manage"><WebsiteManager /></ProtectedRoute>} />
        <Route path="/jira-freeagent-setup" element={<ProtectedRoute permission="jira-freeagent.manage"><JiraFreeAgentSetupPage /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute permission="projects.manage"><ProjectsPage /></ProtectedRoute>} />
        <Route path="/categories" element={<ProtectedRoute permission="categories.manage"><CategoriesPage /></ProtectedRoute>} />
        <Route path="/freeagent-projects-estimates" element={<ProtectedRoute permission="finance.manage"><FreeAgentProjectsEstimatesPage /></ProtectedRoute>} />
        <Route path="/labour-costs" element={<ProtectedRoute permission="labour-costs.manage"><LabourCostsPage /></ProtectedRoute>} />
        <Route path="/operational-automations" element={<ProtectedRoute permission={['finance.manage', 'jira-freeagent.manage', 'projects.manage']}><OperationalAutomationsPage /></ProtectedRoute>} />
        <Route path="/integrated-management-system" element={<ProtectedRoute permission={['integrated-management-system.manage', 'integrated-management-system.audit']}><IntegratedManagementSystemPage /></ProtectedRoute>} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <AppShell />
  </AuthProvider>
);

export default App;
